import { ModuleManifest } from '@airin/shared-types'
import leadgenTranslations from './i18n'

/**
 * Declarative manifest for the lead-generation module.
 *
 * The manifest describes everything the host application needs to know about
 * the module: navigation, configuration schema, theme extensions, translations,
 * and lifecycle hooks. This keeps module onboarding declarative and makes the
 * same metadata available to the admin center, backend, and documentation.
 */
export const manifest: ModuleManifest & {
  translations: {
    en: Record<string, any>
    de: Record<string, any>
  }
} = {
  key: 'leadgen',
  name: 'Lead-Generation',
  version: '1.0.0',
  description:
    'Lead requests with email delivery for evaluation reports. Users can submit an email address and receive the generated report by mail.',
  author: 'TUEV AI Lab',

  // The module depends on the requirements feature being at least configurable.
  dependencies: [
    {
      moduleKey: 'requirements',
      minStatus: 'configuring'
    }
  ],

  // These fields must be configured before the module can move to "live".
  requiredSettings: [
    {
      key: 'senderEmail',
      label: 'leadgen.manifest.settings.senderEmail.label',
      type: 'email',
      description: 'From this address the reports are sent to end users.',
      validation: {
        required: true,
        pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        patternMessage: 'leadgen.manifest.settings.senderEmail.patternMessage'
      }
    }
  ],

  optionalSettings: [
    {
      key: 'recipientEmail',
      label: 'leadgen.manifest.settings.recipientEmail.label',
      type: 'email',
      description:
        'For option 1 and option 2 this address receives the user email, name, and consent text for GDPR-compliant lead forwarding. It is not required for the list-only mode.',
      validation: {
        pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        patternMessage: 'leadgen.manifest.settings.recipientEmail.patternMessage'
      }
    },
    {
      key: 'replyToEmail',
      label: 'leadgen.manifest.settings.replyToEmail.label',
      type: 'email',
      description:
        'Recipient replies are routed to this address. If empty, the sender address is used.',
      validation: {
        pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        patternMessage: 'leadgen.manifest.settings.replyToEmail.patternMessage'
      }
    },
    {
      key: 'nameFieldMode',
      label: 'leadgen.manifest.settings.nameFieldMode.label',
      type: 'string',
      description: 'hidden = not shown, optional = visible but optional, required = mandatory',
      defaultValue: 'optional'
    },
    {
      key: 'consentMode',
      label: 'leadgen.manifest.settings.consentMode.label',
      type: 'string',
      description: 'single = one checkbox, double = two checkboxes, list-only = mailing list only',
      defaultValue: 'single'
    },
    {
      key: 'consentTexts',
      label: 'leadgen.manifest.settings.consentTexts.label',
      type: 'object' as never,
      description: 'Language keyed object, for example: { de: "...", en: "..." }'
    },
    {
      key: 'marketingConsentTexts',
      label: 'leadgen.manifest.settings.marketingConsentTexts.label',
      type: 'object' as never,
      description: 'Only used in double-consent mode. Language keyed object.'
    }
  ],

  // Module menu extensions are merged into the main admin-center navigation.
  menuItems: [
    {
      key: 'leadgen-overview',
      label: 'leadgen.manifest.menuItems.overview',
      icon: 'pi pi-envelope',
      path: '/tenant/lead-generation',
      order: 50,
      roles: ['ai_lab_admin', 'mandant_admin']
    },
    {
      key: 'leadgen-settings',
      label: 'leadgen.manifest.menuItems.settings',
      icon: 'pi pi-cog',
      path: '/tenant/lead-generation/settings',
      parent: 'leadgen-overview',
      order: 1,
      roles: ['ai_lab_admin', 'mandant_admin']
    },
    {
      key: 'leadgen-privacy',
      label: 'leadgen.manifest.menuItems.privacy',
      icon: 'pi pi-shield',
      path: '/tenant/lead-generation/privacy-settings',
      parent: 'leadgen-overview',
      order: 2,
      roles: ['ai_lab_admin', 'mandant_admin']
    },
    {
      key: 'leadgen-templates',
      label: 'leadgen.manifest.menuItems.templates',
      icon: 'pi pi-file',
      path: '/tenant/lead-generation/mail-template',
      parent: 'leadgen-overview',
      order: 3,
      roles: ['ai_lab_admin', 'mandant_admin']
    }
  ],

  // Module-specific theme tokens can extend tenant styling.
  themeSettings: [
    {
      key: 'leadgen_button_bg_color',
      label: 'leadgen.manifest.themeSettings.leadgenButtonBgColor.label',
      type: 'color',
      category: 'frontend',
      defaultValue: '#007bff',
      cssClass: 'leadgen-button',
      cssType: 'background-color'
    }
  ],

  flags: [],

  // Module translations are registered into the shared admin-center i18n instance.
  translations: {
    en: leadgenTranslations.en,
    de: leadgenTranslations.de
  },

  hooks: {
    onActivate: 'onModuleActivate',
    onGoLive: 'onModuleGoLive',
    onConfigChange: 'onConfigChange'
  }
}

export default manifest
